import * as Sentry from '@sentry/capacitor';
import { AxiosError, HttpStatusCode } from 'axios';
import { TFunction } from 'i18next';
import {
  formatPhoneNumber,
  getCurrentLocationAsShareUrl,
  getErrorsListFromServerResponse,
  isSafari,
  removeAddressCountry,
} from 'shared/utils';
import { getAbsoluteUrl } from 'shared/getAbsoluteUrl';
import { ShareUrlValues } from 'hooks/useShareOrCopy';
import {
  Link,
  Region,
  WithStringDates,
  FormEvacZoneStatus,
} from 'shared/types';
import { differenceBy } from 'es-toolkit/compat';
import { attemptAsync } from 'es-toolkit/util';
import { isEqual } from 'es-toolkit';
import { Flooding } from 'features/flooding/flooding.types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { Hazard } from 'features/hazards/hazard.types';
import { getTimeAgo } from 'shared/dates';
import { MutableRefObject, RefObject } from 'react';
import { MAP_ENTITY_DRAWER_MOBILE_DRAWER_ID } from 'features/mapping/components/MapEntityDrawer';
import { Location } from 'features/locations/location.types';
import { fetchLatLngMetadata, LatLngMetadata } from 'hooks/useLatLngMetadata';
import { NotificationType } from 'features/notifications/notifications.types';
import { LinkTypes } from '../../constants';
import { GeoEventType } from './geoEvent.types';
import { AnyGeoEvent, AnyListItemGeoEvent } from './anyGeoEvent.types';

export type TabsScrollTopArgs = {
  scrollElementRef: MutableRefObject<Element | undefined> | null;
  containerRefs: {
    evacInfo?: RefObject<HTMLDivElement>;
    progressionModelAlert?: RefObject<HTMLDivElement>;
    supportUsBanner?: RefObject<HTMLDivElement>;
  };
  baseTop: number;
  nativeOffset?: number;
  evacInfoMargin?: number;
};

const GEO_EVENT_TYPE_TO_PATH_PREFIX: Record<GeoEventType, string> = {
  wildfire: 'i',
  flooding: 'i',
  hazard: 'i',
  location: 'location',
};

const normalizeAddress = (address?: string): string | undefined =>
  address?.replace(/unnamed road,?\s*/i, '') || undefined;

/**
 * Strips administrative-division suffixes (e.g. "County", "Parish", "Borough")
 * from a region display name so it reads naturally when used as an address
 * fallback. Preserves the original casing and spacing, e.g.
 * "Sonoma County" -> "Sonoma".
 */
const getRegionDisplayNameWithoutType = (displayName: string): string =>
  displayName
    .replace(/\b(county|parish|city and borough|borough|census area)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

export const getAddressWithoutStateZipCountry = (
  geocodeResult: google.maps.GeocoderResult,
): string | undefined => {
  const allowedTypes = [
    'street_number',
    'route',
    'locality',
    'sublocality',
    'neighborhood',
  ];

  const components = geocodeResult.address_components.filter((comp) =>
    comp.types.some((t) => allowedTypes.includes(t)),
  );

  const streetNumber = components.find((c) =>
    c.types.includes('street_number'),
  )?.short_name;

  const route = components.find((c) => c.types.includes('route'))?.short_name;

  const otherParts = components
    .filter(
      (c) => !c.types.includes('street_number') && !c.types.includes('route'),
    )
    .map((c) => c.short_name);

  const street =
    streetNumber && route
      ? `${streetNumber} ${route}`
      : streetNumber || route || '';

  return [street, ...otherParts].filter(Boolean).join(', ') || undefined;
};

const getReverseGeocodedAddress = async (
  lat: number,
  lng: number,
  geoEventType: GeoEventType,
): Promise<string | undefined> => {
  if (!window.google) {
    return undefined;
  }
  const geocoder = new window.google.maps.Geocoder();
  const { results } = await geocoder.geocode({ location: { lat, lng } });

  const place =
    results.find(
      (result) =>
        !result.types.includes('plus_code') &&
        Boolean(
          result.address_components.find((component) =>
            component.types.includes('administrative_area_level_2'),
          ),
        ),
    ) || results[0];

  if (!place) {
    return undefined;
  }

  const formattedAddress =
    geoEventType === 'wildfire'
      ? // Wildfire incidents use a shortened address (no state/zip/country).
        // This can be undefined for rural/unincorporated areas with no
        // street-level result; callers fall back to the region name then.
        getAddressWithoutStateZipCountry(place)
      : removeAddressCountry(place.formatted_address);

  return normalizeAddress(formattedAddress);
};

export const getMatchingRegions = async <T extends Pick<Region, 'name' | 'id'>>(
  lat: number,
  lng: number,
  regionOptions: T[],
  geoEventType: GeoEventType,
): Promise<{ placeName: string; matchingRegions: T[] }> => {
  const geocodePromise = attemptAsync<string | undefined, Error>(() =>
    getReverseGeocodedAddress(lat, lng, geoEventType),
  );
  const metadataPromise = attemptAsync<LatLngMetadata, Error>(() =>
    // Region matching only needs `metadata.region`, not the address field.
    fetchLatLngMetadata({ lat, lng }, false),
  );

  const [metadataError, metadata] = await metadataPromise;

  if (metadataError) {
    throw metadataError;
  }

  const [geocodeError, reverseGeocodedAddress] = await geocodePromise;

  if (geocodeError) {
    // Reverse-geocoding is best-effort. Log it but keep going - we still have a
    // valid region from the backend metadata and can fall back to its name.
    Sentry.captureMessage('reverse-geocode-failed', {
      level: 'warning',
      extra: { lat, lng, geoEventType, error: geocodeError?.message },
    });
  }

  const metadataRegion = metadata?.region;
  const metadataRegionId = metadataRegion?.id;

  // The reverse-geocoded address can be empty (e.g. rural or unincorporated
  // areas). Fall back to the metadata region name (without its "County"/etc.
  // suffix) so the address is never blank.
  const placeName =
    reverseGeocodedAddress ||
    (metadataRegion
      ? getRegionDisplayNameWithoutType(metadataRegion.displayName)
      : '');

  const matchingRegions = metadataRegionId
    ? regionOptions.filter((region) => region.id === metadataRegionId)
    : [];

  if (!matchingRegions.length) {
    Sentry.captureMessage('no-matching-regions-found', {
      extra: { lat, lng, metadataRegionId, placeName },
    });
  }

  return { placeName, matchingRegions };
};

type GetMessageFromGeoEventSaveErrorResponseOptions = {
  defaultMessage?: string;
  isProvisional?: boolean;
};

export const getMessageFromGeoEventSaveErrorResponse = (
  error: AxiosError,
  t: TFunction,
  options: GetMessageFromGeoEventSaveErrorResponseOptions = {},
): string => {
  const { defaultMessage, isProvisional = false } = options;
  const errorStatus = error.response?.status;

  if (errorStatus === HttpStatusCode.Forbidden) {
    if (isProvisional) {
      return 'Error 403: You are not authorized to submit incidents for review in this county.';
    }
    return 'Error 403: You cannot submit or update incidents in this county.';
  }

  if (errorStatus === HttpStatusCode.BadRequest) {
    const errors = error?.response?.data as Record<string, string[]> | string[];

    const errorsList = getErrorsListFromServerResponse(errors);

    if (!errorsList?.length) {
      const stringifiedError = JSON.stringify(error.response);
      return `Error 400: Cannot process submission. Double check incident details and try again. Details: ${stringifiedError}`;
    }

    const formattedErrors = errorsList
      .map((err: string) => {
        if (typeof err === 'string') {
          return err.replaceAll('_', ' ').replaceAll('-', ' ').trim();
        }
        return '';
      })
      .filter(Boolean);

    const joinedErrors = formattedErrors.join(', ');
    return `Error 400: Cannot process submission. Reason(s): ${joinedErrors}`;
  }

  return error.message || defaultMessage || t('common.errorMessage');
};

// Parses and formats links from geo event data for form usage.
export const parseFormLinks = (links?: Link[]): Link[] =>
  links?.map((link) => ({
    ...link,
    ...(link.linkType === 'phone'
      ? { value: formatPhoneNumber(link.value) }
      : {}),
  })) ?? [];

// Parses and formats links from form values for geo event data usage.
export const normalizeLinkValues = (links: Link[]): Link[] =>
  links.map((link) => {
    if (link.linkType === LinkTypes.website) {
      return { ...link, value: getAbsoluteUrl(link.value) };
    }
    return link;
  });

const PROVISIONAL_EDIT_IGNORED_FIELDS = [
  'expandLinks',
  'expandInternalLinks',
  'confirmSelectedZones',
  'confirmAdditionalRegions',
  'confirmRemovedRegions',
  'releasePendingZoneUids',
] as const;

type ComparableFormValues = {
  regions?: { id: number }[];
  manualDeactivationStarted?: boolean | null;
  links?: Link[];
  internalLinks?: Link[];
  [key: string]: unknown;
};

const normalizeLinksForComparison = (
  links?: Link[],
): Pick<Link, 'linkType' | 'label' | 'value'>[] =>
  (links ?? []).map(({ linkType, label, value }) => ({
    linkType,
    label,
    value,
  }));

/**
 * Produces a shape-stable snapshot of form values for change detection.
 *
 * React Hook Form's `isDirty` deep-compares against the initial values, which
 * falsely reports some fields as still dirty after the user reverts them:
 * - Counties: the autocomplete stores the full `Region` option object, which
 *   has more fields than the `GeoEventRegion` the form is seeded with, so a
 *   re-added county never deep-equals the original. We compare by region id.
 * - Manual deactivation: the checkbox emits `false` while the seeded value is
 *   `null`, so unchecking never returns to the original. We coerce to boolean.
 * - Links: only the link content matters, so we drop field-array metadata.
 */
const normalizeFormValuesForComparison = (
  values: ComparableFormValues,
): Record<string, unknown> => {
  const normalized: Record<string, unknown> = { ...values };

  PROVISIONAL_EDIT_IGNORED_FIELDS.forEach((field) => {
    delete normalized[field];
  });

  if ('regions' in values) {
    normalized.regions = (values.regions ?? [])
      .map((region) => region.id)
      .sort((a, b) => a - b);
  }
  if ('manualDeactivationStarted' in values) {
    normalized.manualDeactivationStarted = Boolean(
      values.manualDeactivationStarted,
    );
  }
  if ('links' in values) {
    normalized.links = normalizeLinksForComparison(values.links);
  }
  if ('internalLinks' in values) {
    normalized.internalLinks = normalizeLinksForComparison(
      values.internalLinks,
    );
  }

  return normalized;
};

/**
 * Whether the form holds a meaningful edit relative to its initial values.
 *
 * Used to gate provisional (moderated reporter) submissions so an unchanged
 * form can't be submitted as an empty edit. More reliable than RHF's `isDirty`
 * for fields whose values don't round-trip to a deep-equal representation when
 * reverted (see {@link normalizeFormValuesForComparison}).
 */
export const provisionalEditHasChanges = (
  currentValues: ComparableFormValues,
  initialValues: ComparableFormValues,
): boolean =>
  !isEqual(
    normalizeFormValuesForComparison(currentValues),
    normalizeFormValuesForComparison(initialValues),
  );

/**
 * Whether a provisional (moderated reporter) submission should be disabled
 * because the form holds no meaningful change yet.
 */
export const provisionalEditIsDisabled = (params: {
  isProvisional: boolean;
  isEditing: boolean;
  isDirty: boolean;
  currentValues: ComparableFormValues;
  initialValues: ComparableFormValues;
}): boolean => {
  const { isProvisional, isEditing, isDirty, currentValues, initialValues } =
    params;

  if (!isProvisional || !isEditing) {
    return false;
  }

  return !(isDirty && provisionalEditHasChanges(currentValues, initialValues));
};

export const isWildfireGeoEvent = (
  geoEvent: Partial<AnyGeoEvent> | Partial<AnyListItemGeoEvent>,
): geoEvent is Wildfire => {
  return geoEvent.data?.geoEventType === 'wildfire';
};

export const isFloodingGeoEvent = (
  geoEvent: Partial<AnyGeoEvent> | Partial<AnyListItemGeoEvent>,
): geoEvent is Flooding => {
  return geoEvent.data?.geoEventType === 'flooding';
};

export const isHazardGeoEvent = (
  geoEvent: Partial<AnyGeoEvent> | Partial<AnyListItemGeoEvent>,
): geoEvent is Hazard => {
  return geoEvent.data?.geoEventType === 'hazard';
};

export const isLocationGeoEvent = (
  geoEvent: Partial<AnyGeoEvent> | Partial<AnyListItemGeoEvent>,
): geoEvent is Location => {
  return geoEvent.data?.geoEventType === 'location';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getRedundancyMessage = (errorResponse: any): string[] => {
  const { data: errorData } = errorResponse;

  if (errorData.length === 1) {
    const recentGeoEvent = errorData[0];
    const creator = recentGeoEvent.userCreated?.email || 'Someone';
    const timeSince = getTimeAgo(recentGeoEvent.dateCreated);

    return [
      `${creator} just posted ${timeSince}:`,
      `"${recentGeoEvent.name}"`,
      'Are you sure you still want to post this incident?',
    ];
  }

  return [
    `${errorData.length} incidents have been created in the past few minutes for this county!`,
    `Are you sure you still want to post this incident?`,
  ];
};

export const getInitialEvacZonesVisibleState = (
  geoEvent?: Wildfire | Flooding,
): boolean => {
  if (!geoEvent) return false;

  const { data, evacZoneStatuses } = geoEvent;

  const evacDataFlags = [
    data?.temporarilyDisplayEvacZones,
    data?.evacuationOrders,
    data?.evacuationWarnings,
    data?.evacuationAdvisories,
    data?.evacuationShelterInPlace,
    data?.evacuationNotes,
  ];

  const hasEvacData = evacDataFlags.some(Boolean);
  const hasEvacZonesWithStatus = (evacZoneStatuses?.length ?? 0) > 0;

  return hasEvacData || hasEvacZonesWithStatus;
};

export const getViewGeoEventLink = (
  geoEventType: GeoEventType,
  geoEventId: number,
): string => {
  const viewPrefix = GEO_EVENT_TYPE_TO_PATH_PREFIX[geoEventType];
  return `/${viewPrefix}/${geoEventId}`;
};

export const shareGeoEvent = ({
  name,
  address,
  dateModified,
  shareOrCopyUrl,
  t,
}: {
  name: string;
  address?: string | null;
  dateModified: Date;
  shareOrCopyUrl: (values: ShareUrlValues) => Promise<void>;
  t: TFunction;
}): void => {
  const hashTag = `#${name.replace(/\s+/g, '')}`;
  const shareText = address ? `${name} @ ${address} - ${hashTag}` : name;

  shareOrCopyUrl({
    androidDialogTitle: t('geoEvent.shareIncident'),
    title: name,
    text: shareText,
    url: getCurrentLocationAsShareUrl(new Date(dateModified)),
  });
};

const getElementHeight = (ref?: RefObject<HTMLDivElement>): number =>
  ref?.current?.getBoundingClientRect().height || 0;

// Determines a platform-specific scroll correction to normalize vertical scroll
// alignment across browsers.
//
// Mobile and small-screen browsers (Chromium-based and Firefox) apply slightly
// different scroll offset rounding than Safari and desktop. This often causes
// the final scroll position to land a few pixels lower than expected.
// A small (-16px) correction is applied in those environments for consistency.
const getScrollCorrection = (): number => {
  const isMobile = Boolean(
    document.getElementById(MAP_ENTITY_DRAWER_MOBILE_DRAWER_ID),
  );
  const isProblematicBrowser = !isSafari();
  return isProblematicBrowser && isMobile ? -16 : 0;
};

export const getTabsScrollTop = (args: TabsScrollTopArgs): number => {
  const {
    scrollElementRef,
    containerRefs: { evacInfo, progressionModelAlert, supportUsBanner },
    baseTop,
    nativeOffset = 0,
    evacInfoMargin = 0,
  } = args;

  const scrollPosition = scrollElementRef?.current?.scrollTop || 0;

  const offsets = {
    evacInfo: Math.max(0, getElementHeight(evacInfo) - evacInfoMargin),
    progressionModel: getElementHeight(progressionModelAlert),
    supportUsBanner: getElementHeight(supportUsBanner),
  };

  const totalOffset = Object.values(offsets).reduce(
    (sum, offset) => sum + offset,
    0,
  );

  const maxScrollTop =
    baseTop + nativeOffset + totalOffset + getScrollCorrection();

  return Math.min(scrollPosition, maxScrollTop);
};

/**
 * Generic type for child geo events that can be validated for evac zone coverage.
 * Both RelatedWildfire and RelatedFlooding satisfy this interface.
 */
type ChildGeoEventWithRegions = {
  id: number;
  name: string;
  regions: { id: number; displayName: string }[];
};

/**
 * Validates that removing child geo events won't orphan evacuation zones.
 * Used by both wildfire (Complex) and flooding (Parent) incident forms.
 */
export const validateChildGeoEventsForEvacZones = <
  T extends ChildGeoEventWithRegions,
>(params: {
  childGeoEvents: T[];
  evacZoneStatuses: FormEvacZoneStatus[];
  originalChildGeoEvents?: T[];
}): {
  valid: boolean;
  missingRegionNames?: string;
  missingChildIncidents?: string;
} => {
  const {
    childGeoEvents,
    evacZoneStatuses,
    originalChildGeoEvents = [],
  } = params;

  if (!childGeoEvents.length || !evacZoneStatuses.length) {
    return { valid: true };
  }

  const coveredRegionIds = new Set(
    childGeoEvents.flatMap((ge) => ge.regions.map((r) => r.id)),
  );

  const missingRegionIds = new Set(
    evacZoneStatuses
      .map((z) => z.evacZone.regionId)
      .filter((id) => !coveredRegionIds.has(id)),
  );

  if (missingRegionIds.size === 0) {
    return { valid: true };
  }

  const uniqueMissingNames = new Set(
    originalChildGeoEvents
      .flatMap((ge) => ge.regions)
      .filter((r) => missingRegionIds.has(r.id))
      .map((r) => r.displayName),
  );

  const missingRegionNames = Array.from(uniqueMissingNames).join(', ');

  const missingChildIncidents = differenceBy(
    originalChildGeoEvents,
    childGeoEvents,
    'id',
  )
    .map((ge) => ge.name)
    .join(', ');

  return { valid: false, missingRegionNames, missingChildIncidents };
};

export const parseGeoEventDates = <
  T extends {
    dateCreated: Date;
    dateModified: Date;
  },
>(
  geoEvent: WithStringDates<T>,
): T =>
  ({
    ...geoEvent,
    dateCreated: new Date(geoEvent.dateCreated),
    dateModified: new Date(geoEvent.dateModified),
  }) as T;

export const isGeoEventUpgradedFromSilent = (input: {
  previousNotificationType?: NotificationType;
  currentNotificationType: NotificationType;
}): boolean => {
  const { previousNotificationType, currentNotificationType } = input;

  return (
    previousNotificationType === NotificationType.Silent &&
    currentNotificationType === NotificationType.Normal
  );
};
